import React, {
  FunctionComponent,
  useState,
  useContext,
  ChangeEvent,
} from "react";
import { Button, Choice } from "@cmsgov/ds-medicare-gov";
import { useTranslate, sendTealiumEvent } from "../../helpers";
import { CollapsibleBox } from "../../components";
import { PlanSNPType, ActionType } from "../../@types";
import { AppStore } from "../../app/store";

const SpecialNeedsPlansFilters: FunctionComponent<{
  onSpecialNeedsPlansUpdated: () => void;
}> = ({ onSpecialNeedsPlansUpdated }) => {
  const {
    state: { snpFilters = [] },
    dispatch,
  } = useContext(AppStore.AppContext);
  const [isCollapsibleOpen, setIsCollapsibleOpen] = useState(
    snpFilters.length > 1
  );
  const t = useTranslate();

  function updateFilters(filters: PlanSNPType[]): void {
    if (!filters.find(filter => filter === PlanSNPType.SNP_TYPE_NOT_SNP)) {
      filters.push(PlanSNPType.SNP_TYPE_NOT_SNP);
    }
    dispatch({
      type: ActionType.UPDATE_SNP_FILTERS,
      payload: filters,
    });
  }

  function togglePlanType(event: ChangeEvent<HTMLInputElement>): void {
    const { checked, name } = event.currentTarget;
    if (checked) {
      updateFilters([...snpFilters, name as PlanSNPType]);
    } else {
      updateFilters(snpFilters.filter(filter => filter !== name));
    }
    onSpecialNeedsPlansUpdated();
  }

  function clearPlanTypes(): void {
    updateFilters([]);
    onSpecialNeedsPlansUpdated();
  }

  function closeCollapsibleBox(): void {
    // istanbul ignore next: tested elsewhere
    sendTealiumEvent("Find Plans - Available Plans", "Add Special Needs");
    setIsCollapsibleOpen(false);
  }

  function onCollapsibleOpening(): void {
    setIsCollapsibleOpen(true);
  }

  function onCollapsibleClosing(): void {
    setIsCollapsibleOpen(false);
  }

  return (
    <div className="SpecialNeedsPlansFilters mct-hide-print">
      <CollapsibleBox
        labelId="search_results_page.add_special_needs_plans"
        titleClassname="SpecialNeedsPlansFilters__header"
        contentClassname="SpecialNeedsPlansFilters__body"
        open={isCollapsibleOpen}
        onOpening={onCollapsibleOpening}
        onOpen={(): void => {
          sendTealiumEvent(
            "Find Plans - Available Plans",
            "Special Needs Plans"
          );
        }}
        onClosing={onCollapsibleClosing}
      >
        <div className="SpecialNeedsPlansFilters__options">
          <Choice
            type="checkbox"
            name={PlanSNPType.SNP_TYPE_DUAL_ELIGIBLE}
            checked={
              !!snpFilters.find(
                filter => filter === PlanSNPType.SNP_TYPE_DUAL_ELIGIBLE
              )
            }
            onChange={(e: ChangeEvent<HTMLInputElement>): void => {
              if (e.currentTarget.checked) {
                sendTealiumEvent(
                  "Find Plans - Available Plans",
                  "Plans for both Medicare and Medicaid"
                );
              }
              togglePlanType(e);
            }}
            className="SpecialNeedsPlansFilters__filterOption"
            value=""
          >
            {t("search_results_page.plans_for_both_medicare_and_medicaid")}
          </Choice>
          <Choice
            type="checkbox"
            name={PlanSNPType.SNP_TYPE_CHRONIC_OR_DISABLING}
            checked={
              !!snpFilters.find(
                filter => filter === PlanSNPType.SNP_TYPE_CHRONIC_OR_DISABLING
              )
            }
            onChange={(e: ChangeEvent<HTMLInputElement>): void => {
              if (e.currentTarget.checked) {
                sendTealiumEvent(
                  "Find Plans - Available Plans",
                  "Plans for Chronic Conditions"
                );
              }
              togglePlanType(e);
            }}
            className="SpecialNeedsPlansFilters__filterOption"
            value=""
          >
            {t("search_results_page.plans_for_disabling_conditions")}
          </Choice>
          <Choice
            type="checkbox"
            name={PlanSNPType.SNP_TYPE_INSTITUTIONAL}
            checked={
              !!snpFilters.find(
                filter => filter === PlanSNPType.SNP_TYPE_INSTITUTIONAL
              )
            }
            onChange={(e: ChangeEvent<HTMLInputElement>): void => {
              if (e.currentTarget.checked) {
                sendTealiumEvent(
                  "Find Plans - Available Plans",
                  "Plans for Long Term Care"
                );
              }
              togglePlanType(e);
            }}
            className="SpecialNeedsPlansFilters__filterOption"
            value=""
          >
            {t("search_results_page.plans_for_long_term_care_facilities")}
          </Choice>
        </div>
        <div
          className={`PlanFilters__buttons" +
            ${
              isCollapsibleOpen
                ? "ds-u-visibility--visible"
                : "ds-u-visibility--hidden ds-u-display--none"
            }`}
        >
          <Button
            variation="transparent"
            className="ds-c-button--primary-alt ds-u-padding--0 ds-u-margin-right--2"
            onClick={clearPlanTypes}
          >
            {t("search_results_page.clear")}
          </Button>
          <Button
            className="ds-c-button--primary"
            onClick={closeCollapsibleBox}
          >
            {t("search_results_page.add_special_needs_plans")}
          </Button>
        </div>
      </CollapsibleBox>
    </div>
  );
};

export default SpecialNeedsPlansFilters;
